import type { TowerDesign } from "@/game/towerSpec";

/**
 * LATTICE TRANSMISSION PYLON — geometry from a real CAD file: the `tower_a` lattice
 * pylon in highvoltage.dxf (github Zygo/xscreensaver), parsed with ezdxf. A classic
 * double-circuit lattice suspension tower: a square-plan tapered body with THREE
 * conductor crossarms.
 *
 * Built parametrically from the DXF-extracted profile + crossarm heights (NOT the
 * raw mesh edges — that mesh has dangling conductor-arm nodes, so as a pin-jointed
 * truss it's a mechanism that tips over). The measured geometry:
 *   - Body taper (r/Rbase, base→top): 1.00, 0.92, 0.89, 0.84, 0.79, 0.71, 0.68,
 *     0.62, 0.55, 0.50, 0.41, 0.25 — near-linear standard lattice taper.
 *   - THREE crossarm levels at 48% / 63% / 80% of height, reaching 1.70× / 2.36× /
 *     1.71× the base half-width.
 * Scaled to a realistic ~50 m double-circuit pylon; galvanised steel angle.
 */

const BASE_R = 3.9; // base circumradius (m), scaled from the DXF footprint (5.5 m square)
const PROF = [1.0, 0.919, 0.892, 0.838, 0.786, 0.708, 0.676, 0.615, 0.546, 0.502, 0.409, 0.251, 0.2];
function profRadius(t: number): number {
  const x = Math.min(PROF.length - 1, Math.max(0, t * (PROF.length - 1)));
  const i = Math.min(PROF.length - 2, Math.floor(x));
  const f = x - i;
  return BASE_R * (PROF[i]! * (1 - f) + PROF[i + 1]! * f);
}
const PANELS = 16;
const radii = Array.from({ length: PANELS + 1 }, (_, i) => profRadius(i / PANELS));

export const design: TowerDesign = {
  id: "transmission-pylon",
  name: "Lattice Transmission Pylon",
  description: "Double-circuit lattice power pylon, geometry from real CAD (highvoltage.dxf).",
  author: "CAD-derived: highvoltage.dxf tower_a (ezdxf-parsed profile + crossarms)",
  spec: {
    type: "lattice",
    height: 50,
    stack: {
      faces: 4,
      panels: PANELS,
      baseRadius: BASE_R,
      topRadius: BASE_R * 0.2,
      radii, // DXF-sampled body taper
      legSection: "angleL6",
      braceSection: "braceHeavy",
      legMaterial: "a572",
      braceMaterial: "a572",
      bracing: "X",
    },
    // the three conductor crossarms (reach × base half-width, from the DXF)
    crossarms: [
      { atHeightFrac: 0.48, radius: 6.6, section: "braceHeavy", material: "a572" },
      { atHeightFrac: 0.63, radius: 9.2, section: "braceHeavy", material: "a572" }, // widest (middle circuit)
      { atHeightFrac: 0.8, radius: 6.6, section: "braceHeavy", material: "a572" },
    ],
    foundation: {
      soil: "dense-sand",
      base: { type: "pad", width: 2.5, diameter: 1, depth: 2.5, concrete: "c30" },
      connection: {
        kind: "embeddedStub", perimeter: 0.6, embedment: 2.5, edgeDistance: 1.2, fc: 30e6,
      },
    },
  },
};
